import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../services/api';

export const login = createAsyncThunk('auth/login', async (userData, { rejectWithValue }) => {
  try {
    const response = await API.post('/auth/login', userData);
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: 'Login failed' });
  }
});

export const register = createAsyncThunk('auth/register', async (userData, { rejectWithValue }) => {
  try {
    const response = await API.post('/auth/register', userData);
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: 'Registration failed' });
  }
});

export const updateProfile = createAsyncThunk('auth/updateProfile', async (userData, { rejectWithValue }) => {
  try {
    const response = await API.put('/users/profile', userData);
    localStorage.setItem('user', JSON.stringify(response.data.data));
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: 'Failed to update profile' });
  }
});

export const deleteAccount = createAsyncThunk('auth/deleteAccount', async (_, { rejectWithValue }) => {
  try {
    await API.delete('/users/profile');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return null;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: 'Failed to delete account' });
  }
});

export const updatePasswordWithOtp = createAsyncThunk('auth/updatePassword', async ({ idToken, newPassword }, { rejectWithValue }) => {
  try {
    const response = await API.put('/auth/update-password-otp', { idToken, newPassword });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: 'Failed to update password' });
  }
});

const initialState = {
  user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null,
  token: localStorage.getItem('token') || null,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Login failed';
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Registration failed';
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Update failed';
      })
      // Delete Account
      .addCase(deleteAccount.fulfilled, (state) => {
        state.user = null;
        state.token = null;
      })
      // Update Password
      .addCase(updatePasswordWithOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePasswordWithOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updatePasswordWithOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Password update failed';
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
